import { formatDistanceToNow } from 'date-fns'
import { AlertCircle, Plus, Search, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SkeletonRows } from '@/components/ui/skeleton'
import {
  useAgentConversations,
  useCreateAgentConversation,
  useDeleteAgentConversation,
} from '../../../../../hooks/useAgentConversation'
import { cn } from '../../../../../lib/utils'

interface Props {
  activeId: string | undefined
}

export function ConversationList({ activeId }: Props) {
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const { conversations, isLoading } = useAgentConversations({
    q: search.trim() || undefined,
  })
  const createMutation = useCreateAgentConversation()
  const deleteMutation = useDeleteAgentConversation()

  const handleNew = () => {
    createMutation.mutate(undefined, {
      onSuccess: (conv) => navigate(`/insure/internal/agent-chat/${conv.id}`),
    })
  }

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    deleteMutation.mutate(id, {
      onSuccess: () => {
        if (id === activeId) navigate('/insure/internal/agent-chat')
      },
    })
  }

  return (
    <div className="bg-card flex h-full flex-col rounded-lg border">
      <div className="flex items-center gap-2 border-b p-3">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="pl-8"
          />
        </div>
        <Button size="icon" variant="default" onClick={handleNew} disabled={createMutation.isPending}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <SkeletonRows className="p-3" rows={6} rowClassName="h-11" />
        ) : conversations.length === 0 ? (
          <div className="text-muted-foreground p-6 text-center text-sm">
            {search ? 'No matches' : 'No conversations yet'}
          </div>
        ) : (
          <ul className="divide-y">
            {conversations.map((conv) => (
              <li
                key={conv.id}
                onClick={() => navigate(`/insure/internal/agent-chat/${conv.id}`)}
                className={cn(
                  'group hover:bg-muted/50 flex cursor-pointer items-start gap-2 p-3',
                  conv.id === activeId && 'bg-muted'
                )}
              >
                <div className="flex-1 overflow-hidden">
                  <div className="flex items-center gap-1.5">
                    {conv.pendingConfirmation && (
                      <AlertCircle
                        className="h-3.5 w-3.5 shrink-0 text-amber-500"
                        aria-label="Pending confirmation"
                      />
                    )}
                    <div className="truncate text-sm font-medium">{conv.title}</div>
                  </div>
                  {conv.lastMessagePreview && (
                    <div className="text-muted-foreground mt-0.5 truncate text-xs">
                      {conv.lastMessagePreview}
                    </div>
                  )}
                  <div className="text-muted-foreground mt-0.5 text-xs">
                    {formatDistanceToNow(new Date(conv.dateUpdated), { addSuffix: true })}
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="opacity-0 group-hover:opacity-100"
                  onClick={(e) => handleDelete(e, conv.id)}
                  aria-label="Delete conversation"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
